<script>
import ChoiceExam from "@/components/exam/choice-exam.vue";
import EssayExam from "@/components/exam/essay-exam.vue";
import BaseLoading from "@/components/CustomComponent/shared/Loading.vue";
import { useSharedStore } from "@/stores/shared-store";
import { useAuthStore } from "@/stores/auth-store";
import { useExamFetchStore } from "@/stores/exam/exam-fetch-store";
import { useExamDataStore } from "@/stores/exam/exam-data-store";
import Swal from "sweetalert2";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import certificates from "@/components/certificates.vue";

export default {
  props: ["submissionID"],
  components: {
    certificates,
    ChoiceExam,
    EssayExam,
    BaseLoading,
  },
  data() {
    return {
      currentQuestion: null,
      currentIndex: 0,
      allQuestions: [],
      ready: false,
    };
  },
  computed: {
    authStore() {
      return useAuthStore();
    },
    sharedStore() {
      return useSharedStore();
    },
    examFetchStore() {
      return useExamFetchStore();
    },
    examDataStore() {
      return useExamDataStore();
    },
    totalQuestions() {
      return this.allQuestions.length;
    },
    hasQuestions() {
      return this.totalQuestions > 0;
    },
    essayQuestions() {
      return this.allQuestions.filter((q) => q.questionType === "Essay");
    },
    mcQuestions() {
      return this.allQuestions.filter((q) => q.questionType === "MultipleChoice");
    },
    totalEssayCount() {
      return this.essayQuestions.length;
    },
    ungradedEssayCount() {
      return this.essayQuestions.filter((q) => !this.isQuestionGraded(q)).length;
    },
    submissionDetail() {
      return this.examFetchStore.examSubmissionDetail;
    },
    studentName() {
      const studentID = this.submissionDetail?.studentID;
      if (!studentID) return "—";
      return this.authStore.userName(studentID) || "—";
    },
    studentCompany() {
      const studentID = this.submissionDetail?.studentID;
      if (!studentID) return "";
      const student = this.authStore.otherUsers.find((u) => u.id === studentID);
      return student?.company || student?.cmp_code || "";
    },
    courseTitle() {
      return (
        this.submissionDetail?.course_title ||
        this.submissionDetail?.exam?.course_name ||
        "—"
      );
    },
    courseId() {
      const detail = this.submissionDetail;
      return detail?.exam?.courseID ?? detail?.courseID ?? null;
    },
    multipleChoiceScore() {
      return this.submissionDetail?.multipleChoiceScore ?? null;
    },
    gradedCount() {
      return this.essayQuestions.filter((q) => this.isQuestionGraded(q)).length;
    },
    progressPercent() {
      if (!this.totalEssayCount) return 100;
      return Math.round((this.gradedCount / this.totalEssayCount) * 100);
    },
    allEssaysScored() {
      return (
        this.totalEssayCount === 0 ||
        this.essayQuestions.every((q) => this.isQuestionGraded(q))
      );
    },
    canFinish() {
      return this.allEssaysScored;
    },
    isLastQuestion() {
      return this.currentIndex >= this.totalQuestions - 1;
    },
  },
  async mounted() {
    await this.examFetchStore.fetchAnswerListBySubmission(this.submissionID);
    await this.examFetchStore.fetchExamSubmissionsDetail(this.submissionID);

    // Ambil examID dari submissionDetail atau relasi answer
    const examID =
      this.submissionDetail?.exam?.examID ||
      this.submissionDetail?.examID?.examID ||
      this.submissionDetail?.examID ||
      this.submissionDetail?.exam?.id ||
      this.examFetchStore.answerListBySubmission?.[0]?.question?.examID ||
      null;

    if (examID) {
      await this.examFetchStore.fetchQuestionsByExam(examID);
    }

    await this.examDataStore.loadScoring();
    if (
      !this.examDataStore.essayScore ||
      this.examDataStore.essayScore.length === 0
    ) {
      this.examDataStore.initScoring();
    }

    // Buat map lookup dari tabel ExamQuestion (yang menyimpan correctAnswer dan opsi A-D)
    const questionsMap = new Map();
    (this.examFetchStore.examQuestions || []).forEach((q) => {
      const qId = q.questionID || q.id;
      if (qId) {
        questionsMap.set(String(qId), q);
      }
    });

    // Gabungkan data jawaban siswa dengan data soal dari tabel ExamQuestion
    this.allQuestions = (this.examFetchStore.answerListBySubmission || []).map(
      (ans) => {
        const qId = ans.question?.questionID || ans.questionID || ans.question;
        const qData = questionsMap.get(String(qId)) || {};

        const correctAnswer = (
          ans.correctAnswer ||
          qData.correctAnswer ||
          ""
        ).trim().toUpperCase();

        const studentAnswer = (ans.answer || "").trim().toUpperCase();

        let isCorrect = ans.is_correct;
        if (
          (ans.questionType === "MultipleChoice" ||
            qData.questionType === "MultipleChoice") &&
          correctAnswer
        ) {
          isCorrect = studentAnswer === correctAnswer;
        }

        return {
          ...ans,
          question: qId,
          questionID: qId,
          questionText: ans.questionText || qData.questionText,
          questionType: ans.questionType || qData.questionType,
          option_a: ans.option_a || qData.option_a,
          option_b: ans.option_b || qData.option_b,
          option_c: ans.option_c || qData.option_c,
          option_d: ans.option_d || qData.option_d,
          correctAnswer: correctAnswer,
          is_correct: isCorrect,
        };
      }
    );

    if (this.allQuestions.length > 0) {
      this.currentQuestion = this.allQuestions[0];
      this.currentIndex = 0;
    }
    this.ready = true;
  },
  methods: {
    isQuestionGraded(question) {
      if (!question) return false;
      if (question.questionType !== "Essay") return true;
      const questionId = question.questionID || question.question;
      const item = this.examDataStore.essayScore.find(
        (s) => String(s.question) === String(questionId)
      );
      if (!item) return false;
      const score = item.score;
      return score !== null && score !== undefined && score !== "";
    },
    questionPillClass(question, index) {
      const active = index === this.currentIndex;
      if (active) return "bg-main text-white nomor-active shadow-sm";

      // Khusus mode grading: warnai nomor berdasarkan status grading
      if (question.questionType === "Essay") {
        if (this.isQuestionGraded(question)) {
          return "nomor-essay-graded text-success";
        }
        // Soal essay yang belum dinilai -> Warna amber/kuning agar guru langsung tahu yang perlu di-grade
        return "nomor-essay-needs-grading text-dark shadow-sm";
      }

      // Pilihan Ganda: Dinilai otomatis oleh sistem (tidak butuh grading guru)
      return "nomor-mc-auto text-secondary";
    },
    nextQuestion() {
      if (this.currentIndex < this.totalQuestions - 1) {
        this.currentIndex++;
        this.currentQuestion = this.allQuestions[this.currentIndex];
      }
    },
    prevQuestion() {
      if (this.currentIndex > 0) {
        this.currentIndex--;
        this.currentQuestion = this.allQuestions[this.currentIndex];
      }
    },
    goToQuestion(index) {
      this.currentIndex = index;
      this.currentQuestion = this.allQuestions[index];
    },
    handleBack() {
      this.$router.back();
    },
    async handelFinishGrading() {
      if (!this.canFinish) {
        Swal.fire({
          title: "Belum lengkap",
          text: "Semua soal essay harus diberi nilai (0–100) sebelum menyelesaikan penilaian.",
          icon: "info",
          confirmButtonText: "OK",
        });
        return;
      }

      const result = await Swal.fire({
        title: "Konfirmasi",
        text: "Apakah Anda yakin ingin menyelesaikan penilaian essay ini? Nilai tidak bisa diubah lagi setelah disimpan.",
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Ya, simpan nilai",
        cancelButtonText: "Batal",
      });

      if (result.isConfirmed) {
        try {
          // panggil submitScoring dari store
          const response = await this.examDataStore.gradeEssay(
            this.submissionID
          );

          this.examDataStore.clearScoring();

          if (response?.status === "Passed") {
            try {
              await this.handleSaveCertificate();
            } catch (certError) {
              console.error("Gagal generate atau upload sertifikat:", certError);
            }
          }

          Swal.fire({
            title: "Berhasil",
            text: "Penilaian essay berhasil disimpan!",
            icon: "success",
            confirmButtonText: "OK",
          });

          // Redirect ke halaman teacher scoring list
          this.$router.back();
        } catch (error) {
          console.error("Gagal submit scoring:", error);
          Swal.fire({
            title: "Gagal",
            text: "Terjadi kesalahan saat menyimpan penilaian. Silakan coba lagi.",
            icon: "error",
          });
        }
      }
    },
    async handleSaveCertificate() {
      this.sharedStore.startRequest();
      if (!this.$refs.certComponent?.certRef) {
        this.sharedStore.endRequest();
        return;
      }

      const element = this.$refs.certComponent.certRef;

      // 1. Convert ke canvas
      const canvas = await html2canvas(element, {
        scale: 2,
        useCORS: true,
      });

      const imgData = canvas.toDataURL("image/png");

      // 2. Buat PDF dari canvas
      const pdf = new jsPDF({
        orientation: canvas.width > canvas.height ? "landscape" : "portrait",
        unit: "px",
        format: [canvas.width, canvas.height],
      });

      pdf.addImage(imgData, "PNG", 0, 0, canvas.width, canvas.height);

      // 3. Convert PDF ke Blob
      const pdfBlob = pdf.output("blob");

      // 4. Kirim ke backend dengan studentID siswa
      const studentID = this.submissionDetail?.studentID;
      await this.examDataStore.uploadCertificate(pdfBlob, this.courseId, studentID);
      this.sharedStore.endRequest();
    },
  },
};
</script>
<template>
  <div class="grading-page">
    <!-- Sticky header: back + student/course + progress -->
    <section class="grading-header border-bottom bg-white sticky-top">
      <div
        class="d-flex flex-wrap align-items-center justify-content-between gap-3 px-3 px-md-4 py-3"
      >
        <div class="d-flex align-items-start gap-3 min-w-0 flex-grow-1">
          <button
            type="button"
            class="btn btn-link text-decoration-none color-main p-0 d-inline-flex align-items-center grading-back-btn flex-shrink-0 mt-1"
            @click="handleBack"
            aria-label="Back to submissions"
          >
            <i class="isax isax-arrow-left me-1" aria-hidden="true"></i>
            <span>Back</span>
          </button>

          <div class="min-w-0">
            <p class="mb-0 small text-muted text-uppercase fw-semibold">
              Grading Penilaian
            </p>
            <h5 class="mb-0 text-truncate fw-semibold" :title="studentName">
              {{ studentName }}
            </h5>
            <p
              class="mb-0 small text-muted text-truncate"
              :title="courseTitle"
            >
              {{ courseTitle }}
            </p>
          </div>
        </div>

        <div class="d-flex align-items-center gap-3">
          <!-- Skor Pilihan Ganda -->
          <div
            v-if="mcQuestions.length > 0"
            class="d-flex flex-column align-items-end border-end pe-3"
          >
            <span class="small text-muted">Nilai Pilihan Ganda</span>
            <span class="badge text-bg-light border fw-bold text-dark px-2 py-1">
              {{ multipleChoiceScore !== null ? multipleChoiceScore : '-' }} / 100
            </span>
          </div>

          <!-- Progress Penilaian Essay -->
          <div
            v-if="ready && totalEssayCount > 0"
            class="grading-progress flex-shrink-0"
            aria-live="polite"
          >
            <div
              class="d-flex align-items-center justify-content-between gap-2 mb-1"
            >
              <span class="small text-muted">Progress Essay</span>
              <span class="small fw-semibold">
                {{ gradedCount }} / {{ totalEssayCount }} dinilai
              </span>
            </div>
            <div
              class="progress grading-progress-bar"
              role="progressbar"
              :aria-valuenow="gradedCount"
              :aria-valuemin="0"
              :aria-valuemax="totalEssayCount"
              :aria-label="`${gradedCount} of ${totalEssayCount} essays graded`"
            >
              <div
                class="progress-bar bg-main"
                :style="{ width: progressPercent + '%' }"
              ></div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Empty state -->
    <section
      v-if="ready && !sharedStore.loading && !hasQuestions"
      class="grading-empty text-center py-5 px-3"
    >
      <div class="mx-auto" style="max-width: 420px">
        <div class="grading-empty-icon mx-auto mb-3">
          <i class="isax isax-document-text" aria-hidden="true"></i>
        </div>
        <h6 class="mb-2">Tidak ada soal ujian</h6>
        <p class="text-muted mb-4">
          Submission ini tidak memiliki jawaban ujian untuk diperiksa. Kembali ke daftar submission.
        </p>
        <button type="button" class="btn btn-main px-4" @click="handleBack">
          <i class="isax isax-arrow-left me-1" aria-hidden="true"></i>
          Kembali ke Submissions
        </button>
      </div>
    </section>

    <!-- Main grading workspace -->
    <section v-else-if="hasQuestions" class="grading-body row g-0">
      <!-- Question navigator sidebar -->
      <aside class="col-12 col-md-3 border-end py-3 grading-sidebar">
        <div class="px-4 mb-2">
          <div class="d-flex align-items-center justify-content-between mb-1">
            <p class="mb-0 text-muted small fw-semibold text-uppercase">
              Daftar Soal
            </p>
            <span class="badge text-bg-light border text-muted small">
              {{ totalQuestions }} Soal
            </span>
          </div>
          <!-- Ringkasan cepat soal yang perlu dinilai -->
          <div v-if="ungradedEssayCount > 0" class="small text-warning-emphasis fw-semibold d-flex align-items-center gap-1">
            <span class="status-indicator-dot bg-warning"></span>
            <span>{{ ungradedEssayCount }} essay perlu dinilai</span>
          </div>
          <div v-else-if="totalEssayCount > 0" class="small text-success fw-semibold d-flex align-items-center gap-1">
            <span class="status-indicator-dot bg-success"></span>
            <span>Semua essay telah dinilai</span>
          </div>
        </div>

        <div class="d-flex py-2 px-4 gap-2 flex-wrap">
          <button
            v-for="(question, index) in allQuestions"
            :key="question.questionID || question.question || index"
            type="button"
            @click="goToQuestion(index)"
            class="wh-nomor d-flex flex-column justify-content-center align-items-center rounded-2 border position-relative"
            :class="questionPillClass(question, index)"
            :aria-label="`Soal ${index + 1} (${question.questionType})`"
            :aria-current="index === currentIndex ? 'true' : undefined"
          >
            <span class="fw-bolder lh-1">{{ index + 1 }}</span>
            <span class="nomor-sub-badge">{{ question.questionType === 'Essay' ? 'ES' : 'PG' }}</span>

            <!-- Badge tanda seru pada nomor Essay yang PERLU dinilai -->
            <span
              v-if="question.questionType === 'Essay' && !isQuestionGraded(question) && index !== currentIndex"
              class="nomor-badge-warning"
              title="Perlu dinilai"
              aria-hidden="true"
            >
              !
            </span>

            <!-- Dot indikator untuk Essay yang SUDAH dinilai -->
            <span
              v-if="question.questionType === 'Essay' && isQuestionGraded(question) && index !== currentIndex"
              class="nomor-check"
              title="Sudah dinilai"
              aria-hidden="true"
            ></span>
          </button>
        </div>

        <div class="px-4 pt-3">
          <p class="small text-muted fw-semibold text-uppercase mb-2" style="font-size: 0.75rem;">Keterangan Warna</p>
          <ul class="list-unstyled small text-muted mb-0 grading-legend">
            <li class="d-flex align-items-center gap-2 mb-2">
              <span class="legend-swatch bg-main"></span>
              <span class="fw-medium text-dark">Sedang Dilihat</span>
            </li>
            <li class="d-flex align-items-center gap-2 mb-2">
              <span class="legend-swatch nomor-needs-grading"></span>
              <span class="fw-bold text-dark">Perlu Di-grading (Essay)</span>
            </li>
            <li class="d-flex align-items-center gap-2 mb-2">
              <span class="legend-swatch nomor-essay-graded"></span>
              <span>Sudah Di-grading (Essay)</span>
            </li>
            <li class="d-flex align-items-center gap-2">
              <span class="legend-swatch nomor-mc-auto"></span>
              <span>Pilihan Ganda (Otomatis)</span>
            </li>
          </ul>
        </div>
      </aside>

      <!-- Answer + score panel -->
      <div class="col-12 col-md-9 d-flex flex-column grading-main">
        <div class="flex-grow-1">
          <div
            class="d-flex flex-wrap align-items-center justify-content-between gap-2 px-3 px-md-4 pt-3"
          >
            <div class="d-flex align-items-center gap-2">
              <p class="mb-0 small text-muted">
                Soal {{ currentIndex + 1 }} dari {{ totalQuestions }}
              </p>
              <span
                class="badge"
                :class="currentQuestion?.questionType === 'Essay' ? 'text-bg-primary' : 'text-bg-secondary'"
              >
                {{ currentQuestion?.questionType === 'Essay' ? 'Soal Essay' : 'Pilihan Ganda' }}
              </span>
            </div>

            <div class="d-flex align-items-center gap-2">
              <template v-if="currentQuestion?.questionType === 'Essay'">
                <span
                  class="badge rounded-pill"
                  :class="
                    isQuestionGraded(currentQuestion)
                      ? 'text-bg-success'
                      : 'text-bg-warning text-dark'
                  "
                >
                  {{
                    isQuestionGraded(currentQuestion) ? "Sudah Dinilai" : "Perlu Nilai (Wajib)"
                  }}
                </span>
              </template>
              <template v-else-if="currentQuestion?.questionType === 'MultipleChoice'">
                <span
                  class="badge rounded-pill"
                  :class="
                    currentQuestion?.is_correct
                      ? 'text-bg-success'
                      : 'text-bg-danger'
                  "
                >
                  {{ currentQuestion?.is_correct ? "Jawaban Siswa Benar" : "Jawaban Siswa Salah" }}
                </span>
                <span class="badge text-bg-light border text-muted">
                  Dinilai Otomatis
                </span>
              </template>
            </div>
          </div>

          <!-- Komponen Soal: Pilihan Ganda (Read-only review) atau Essay -->
          <ChoiceExam
            v-if="currentQuestion && currentQuestion.questionType === 'MultipleChoice'"
            :quiz="currentQuestion"
            :preview="true"
            :isReview="true"
            :key="'mc-' + (currentQuestion.questionID || currentQuestion.question)"
          />
          <EssayExam
            v-else-if="currentQuestion && currentQuestion.questionType === 'Essay'"
            :quiz="currentQuestion"
            :key="'essay-' + (currentQuestion.questionID || currentQuestion.question)"
          />
        </div>

        <!-- Footer actions -->
        <div
          class="d-flex flex-wrap justify-content-between align-items-center gap-2 mt-auto pt-3 border-top px-3 px-md-4 pb-3 grading-footer"
        >
          <button
            type="button"
            class="btn btn-outline-secondary px-4"
            @click="prevQuestion"
            :disabled="currentIndex === 0"
          >
            Previous
          </button>

          <div class="d-flex flex-wrap gap-2 ms-auto">
            <button
              v-if="!isLastQuestion"
              type="button"
              class="btn btn-outline-primary px-4"
              @click="nextQuestion"
            >
              Next
            </button>
            <button
              type="button"
              class="btn btn-success px-4"
              :disabled="!canFinish"
              :title="
                canFinish
                  ? 'Simpan semua nilai essay'
                  : 'Beri nilai untuk semua soal essay terlebih dahulu'
              "
              @click="handelFinishGrading"
            >
              Finish grading
            </button>
          </div>
        </div>
      </div>
    </section>

    <!-- Off-screen container agar html2canvas bisa merender elemen dengan ukuran nyata -->
    <div style="position: fixed; left: -9999px; top: 0; z-index: -1000; width: 1100px;">
      <certificates
        ref="certComponent"
        :name="studentName"
        :course="courseTitle"
        :date="sharedStore.formatTanggal(new Date())"
        :instructor="authStore.currentUser?.nickname"
        :company="studentCompany"
      />
    </div>
  </div>
  <BaseLoading v-show="sharedStore.loading"></BaseLoading>
</template>
<style scoped>
.grading-page {
  min-height: 100%;
  background-color: #fff;
}

.grading-header {
  z-index: 1020;
}

.bg-main {
  background-color: #ff4667 !important;
}

.color-main {
  color: #ff4667;
}

.btn-main {
  background-color: #ff4667;
  border-color: #ff4667;
  color: #fff;
}

.btn-main:hover,
.btn-main:focus {
  background-color: #e63d5c;
  border-color: #e63d5c;
  color: #fff;
}

.grading-back-btn:hover,
.grading-back-btn:focus {
  color: #e63d5c !important;
}

.grading-progress {
  min-width: 160px;
  max-width: 220px;
  width: 100%;
}

.grading-progress-bar {
  height: 6px;
  background-color: #f1f3f5;
}

.wh-nomor {
  width: 44px;
  height: 44px;
  min-width: 44px;
  cursor: pointer;
  transition: background-color 0.15s ease, color 0.15s ease,
    box-shadow 0.15s ease;
}

.nomor-sub-badge {
  font-size: 0.55rem;
  font-weight: 700;
  letter-spacing: 0.5px;
  opacity: 0.85;
  margin-top: 2px;
}

.wh-nomor:hover:not(.bg-main) {
  opacity: 0.9;
}

.wh-nomor:focus-visible {
  outline: 2px solid #ff4667;
  outline-offset: 2px;
}

/* 1. Essay yang PERLU di-grading oleh guru (Kuning/Amber mencolok) */
.nomor-essay-needs-grading {
  background-color: #fff8e1 !important;
  color: #855302 !important;
  border: 1.5px solid #ffb300 !important;
  font-weight: 600;
}

.nomor-essay-needs-grading:hover {
  background-color: #ffecb3 !important;
}

/* 2. Essay yang SUDAH selesai dinilai oleh guru (Hijau sukses) */
.nomor-essay-graded {
  background-color: #e8f5e9 !important;
  color: #1b5e20 !important;
  border: 1.5px solid #81c784 !important;
}

.nomor-essay-graded:hover {
  background-color: #c8e6c9 !important;
}

/* 3. Pilihan Ganda: Dinilai otomatis oleh sistem (Netral / Soft Gray) */
.nomor-mc-auto {
  background-color: #f8fafc !important;
  color: #64748b !important;
  border: 1px solid #e2e8f0 !important;
}

.nomor-mc-auto:hover {
  background-color: #f1f5f9 !important;
}

/* Badge tanda seru pada nomor Essay yang perlu dinilai */
.nomor-badge-warning {
  position: absolute;
  top: -4px;
  right: -4px;
  width: 14px;
  height: 14px;
  border-radius: 50%;
  background-color: #ff9800;
  color: #fff;
  font-size: 0.65rem;
  font-weight: 800;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.25);
  line-height: 1;
}

/* Dot tanda centang pada Essay yang sudah dinilai */
.nomor-check {
  position: absolute;
  top: 2px;
  right: 3px;
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background-color: #2e7d32;
}

.status-indicator-dot {
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
}

/* Legend Swatches */
.legend-swatch {
  display: inline-block;
  width: 14px;
  height: 14px;
  border-radius: 4px;
  flex-shrink: 0;
}

.legend-swatch.nomor-needs-grading {
  background-color: #fff8e1;
  border: 1.5px solid #ffb300;
}

.legend-swatch.nomor-essay-graded {
  background-color: #e8f5e9;
  border: 1.5px solid #81c784;
}

.legend-swatch.nomor-mc-auto {
  background-color: #f8fafc;
  border: 1px solid #e2e8f0;
}

.grading-empty-icon {
  width: 56px;
  height: 56px;
  border-radius: 12px;
  background-color: #fff0f3;
  color: #ff4667;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
}

.grading-main {
  min-height: 420px;
}

.grading-footer .btn:disabled {
  cursor: not-allowed;
}

@media (max-width: 767.98px) {
  .grading-sidebar {
    border-bottom: 1px solid #dee2e6;
    border-right: 0 !important;
  }

  .grading-progress {
    max-width: none;
  }
}

@media (prefers-reduced-motion: reduce) {
  .wh-nomor {
    transition: none;
  }
}
</style>
